import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import { addLog } from 'assets/components/feedback/Feedback';
import Icon from 'assets/components/icon/Icon';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import Confirm from 'assets/components/page/templates/Confirm';
import Modal from 'assets/components/page/templates/Modal';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemeModalDefault from 'assets/components/page/templates/themes/Theme.Modal.Default';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import { defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { HttpStatus } from 'config/Api.Config';
import dayjs from 'dayjs';
import { omit } from 'lodash';
import PaymentType from 'models/enums/PaymentType';
import ClaimJobPayment from 'models/finance/claimJobPayment/Model.ClaimJobPayment';
import ClaimJobPaymentItem from 'models/finance/claimJobPaymentItem/Model.ClaimJobPaymentItem';
import claimJobPaymentItemProcessUsBankPaymentApi from 'models/finance/claimJobPaymentItem/processUsBankPayment/Api.ClaimJobPaymentItem.ProcessUsBankPayment';
import claimJobPaymentItemSaveApi from 'models/finance/claimJobPaymentItem/save/Api.ClaimJobPaymentItem.Save';
import usBankCancelApi from 'models/integration/usBank/cancel/Api.UsBank.Cancel';
import usBankInfoApi from 'models/integration/usBank/info/Api.UsBank.Info';
import usBankUpdateApi from 'models/integration/usBank/update/Api.UsBank.Update';
import { useEffect, useState } from 'react';

type PaymentData = {
  claimJobPaymentItemId: number;
  email: string;
};

type UsBankAction = 'create' | 'modify' | 'cancel' | 'info';
type ModifyPayment = {
  amount: number;
  usBankVirtualCardId: string;
};

const ClaimJobPaymentItemUsBankPayment = ({
  payment,
  paymentItem,
  reload,
}: {
  payment: ClaimJobPayment;
  paymentItem: ClaimJobPaymentItem;
  reload(): void;
}) => {
  const { lang } = getLocales();
  const { permissions, userId, isSuperAdmin } = useAuthStore();

  const [pendingPayment, setPendingPayment] = useState<PaymentData & { payeeName?: string }>();
  const [modfiyPayment, setModifyPayment] = useState<ModifyPayment>(undefined);
  const [usBankAction, setUsBankAction] = useState<UsBankAction>(undefined);
  const [paymentInfo, setPaymentInfo] = useState<Model.IVirtualCardStatus>(undefined);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const usBankCardId = paymentItem?.usBankVirtualCardId;
  const isOwner = isSuperAdmin || userId === payment?.ownerId;

  async function sendPaymentManually(item: Api.Finance.ClaimJobPaymentItem.Save.IRequest) {
    setIsProcessing(true);
    const res = await claimJobPaymentItemSaveApi(item);
    if (res.status !== HttpStatus.ok) {
      setIsProcessing(false);
      addLog({ error: res.message });
    } else {
      setIsProcessing(false);
      addLog({ success: lang.paymentSent });
      if (reload) reload();
    }
  }

  useEffect(() => {
    if (usBankCardId) {
      (async () => {
        const res = await usBankInfoApi({ usBankCardId });
        if (res.status === HttpStatus.ok) {
          setPaymentInfo({ ...res.payload, isCanceled: res?.payload?.status === 'CANCELED' });
          setModifyPayment({ amount: Number(res.payload.amount), usBankVirtualCardId: usBankCardId });
        }
      })();
    }
  }, [usBankCardId]);

  const isCancellable = isOwner && permissions?.CANCEL_CLAIMJOBPAYMENTITEM_USBANK_PAYMENT && usBankCardId;

  return (
    <>
      <DropdownButton
        title={lang.paymentOptions}
        buttonMedia={<Icon class="fas-money-check-alt" />}
        horizontalPosition="auto_fixed"
        verticalPosition="auto_fixed"
        subcomponents={ThemeDropdownButtonPopup}
        htmlElementProps={{
          title: paymentItem.isPaymentSent
            ? lang.paymentAlreadySent
            : paymentItem.currency !== 'CAD' || paymentItem.paymentMethodCode !== PaymentType.usBank
              ? lang.paymentIsAvailableOnlyFor
              : lang.paymentOptions,
        }}
        disabled={
          paymentItem.isPaymentSentManually ||
          paymentItem.currency !== 'CAD' ||
          paymentItem.paymentMethodCode !== PaymentType.usBank ||
          isProcessing
        }
        dropdownOptions={
          <>
            <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-money-check-alt" />}
              render={lang.createPayment}
              htmlElementProps={{
                button: {
                  title: usBankCardId
                    ? lang.youAlreadyHavePendingPayment
                    : paymentItem.claimJobPaymentId
                      ? lang.alreadyPaid
                      : lang.sendPaymentRequest,
                },
              }}
              disabled={
                paymentItem.isPaymentSent ||
                !!usBankCardId ||
                !(isOwner && permissions?.UPDATE_CLAIMJOBPAYMENTITEM_USBANK_PAYMENT) ||
                !(paymentItem.id && paymentItem.total > 0) ||
                isProcessing
              }
              onClick={(data) => {
                setUsBankAction('create');
                setPendingPayment(data);
              }}
              data={{
                email: payment.paymentEmailAddress || payment.claimJobPaymentPayee?.getEmailForPayment(),
                claimJobPaymentItemId: paymentItem.id,
                payeeName: payment.claimJobPaymentPayee?.displayInfo?.title
                  ? `${payment.claimJobPaymentPayee?.displayInfo?.title} (${paymentItem.total?.toFixed(2)} ${paymentItem.currency})`
                  : undefined,
              }}
            />
            <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-money-check-alt" />}
              render={lang.modifyPayment}
              onClick={setUsBankAction}
              data="modify"
              htmlElementProps={{ title: lang.usBankModifyPayment }}
              disabled={
                paymentInfo?.isCanceled ||
                !(isOwner && permissions?.UPDATE_CLAIMJOBPAYMENTITEM_USBANK_PAYMENT && usBankCardId) ||
                isProcessing
              }
            />
            <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-money-check-alt" />}
              render={lang.infoPayment}
              onClick={setUsBankAction}
              data="info"
              disabled={
                !(
                  isOwner &&
                  (permissions?.UPDATE_CLAIMJOBPAYMENTITEM_USBANK_PAYMENT ||
                    permissions?.CANCEL_CLAIMJOBPAYMENTITEM_USBANK_PAYMENT) &&
                  usBankCardId
                ) || isProcessing
              }
              htmlElementProps={{ title: lang.usBankInfoPayment }}
            />
            {/* <Button
              class={ThemeButtonMenuItem}
              media={<Icon class="fas-money-check-alt" />}
              render={lang.cancelPayment}
              onClick={setUsBankAction}
              data="cancel"
              disabled={!isCancellable}
              htmlElementProps={{ title: lang.usBankCancelPayment }}
            /> */}
            <Button
              onClick={async () =>
                sendPaymentManually({
                  ...paymentItem,
                  isPaymentSentManually: true,
                  paymentRequestedAtUtc: dayjs().utc(false).format(defaultServerDateTimeFormat),
                })
              }
              disabled={paymentItem.isPaymentSent || !(isOwner && permissions?.EDIT_CLAIMJOBPAYMENTITEM)}
              class={ThemeButtonMenuItem}
              render={lang.sendManually}
              media={<Icon class="fas-receipt" />}
              htmlElementProps={{ title: lang.sendManually }}
            />
          </>
        }
      />
      {pendingPayment &&
        isOwner &&
        permissions?.UPDATE_CLAIMJOBPAYMENTITEM_USBANK_PAYMENT &&
        usBankAction === 'create' && (
          <Modal
            header={lang.usBankCreatePayment}
            subcomponents={ThemeModalDefault}
            onClose={() => {
              setPendingPayment(undefined);
              setUsBankAction(undefined);
            }}
            render={
              <FormWrapper>
                <div style={{ textAlign: 'center', fontWeight: 600 }}>{lang.sendPaymentRequestTo}:</div>
                <p style={{ textAlign: 'center' }}>{pendingPayment?.payeeName}</p>
                <TextInput
                  name="email"
                  label={lang.email}
                  value={pendingPayment?.email}
                  error={!pendingPayment?.email && lang.required}
                  onChange={({ email }) => setPendingPayment((prev) => ({ ...prev, email }))}
                />
              </FormWrapper>
            }
            footer={
              <Button
                class={ThemeButtonCircle}
                render={lang.sendPaymentRequest}
                disabled={!pendingPayment?.claimJobPaymentItemId}
                onClick={async () => {
                  setIsProcessing(true);
                  const res = await claimJobPaymentItemProcessUsBankPaymentApi(omit(pendingPayment, 'payeeName'));
                  if (res.status !== HttpStatus.ok) {
                    addLog({ error: res.message });
                    setIsProcessing(false);
                  } else {
                    setIsProcessing(false);
                    if (reload) reload();
                  }
                }}
                isLoading={isProcessing}
                active
              />
            }
          />
        )}
      {modfiyPayment &&
        isOwner &&
        permissions?.UPDATE_CLAIMJOBPAYMENTITEM_USBANK_PAYMENT &&
        usBankAction === 'modify' && (
          <Modal
            header={lang.usBankModifyPayment}
            subcomponents={ThemeModalDefault}
            onClose={() => setUsBankAction(undefined)}
            render={
              <FormWrapper>
                <NumberInput
                  name="amount"
                  label={lang.amount}
                  value={modfiyPayment?.amount}
                  error={!modfiyPayment?.amount && lang.required}
                  onChange={({ amount }) => setModifyPayment((prev) => ({ ...prev, amount }))}
                />
              </FormWrapper>
            }
            footer={
              <Button
                class={ThemeButtonCircle}
                render={lang.modifyPayment}
                disabled={!modfiyPayment?.amount}
                onClick={async () => {
                  setIsProcessing(true);
                  const res = await usBankUpdateApi(
                    { usBankCardId: modfiyPayment?.usBankVirtualCardId },
                    modfiyPayment?.amount
                  );
                  if (res.status !== HttpStatus.ok) {
                    addLog({ error: res.message });
                    setIsProcessing(false);
                  } else {
                    setIsProcessing(false);
                    if (reload) reload();
                  }
                }}
                isLoading={isProcessing}
                active
              />
            }
          />
        )}
      {isCancellable && usBankAction === 'cancel' && (
        <Confirm
          onClose={() => setUsBankAction(undefined)}
          subcomponents={ThemeConfirmDefault}
          actionName={`${lang.cancelPayment} for payee: ${payment.claimJobPaymentPayee?.displayInfo?.title}`}
          confirmationType={'nextButton'}
          onConfirm={async () => {
            setIsProcessing(true);
            const res = await usBankCancelApi({ usBankCardId: usBankCardId });
            if (res.status !== HttpStatus.ok) {
              addLog({ error: res.message });
              setIsProcessing(false);
              setUsBankAction(undefined);
            } else {
              setIsProcessing(false);
              setUsBankAction(undefined);
              if (reload) reload();
            }
          }}
          isLoading={isProcessing}
        />
      )}
      {paymentInfo && usBankAction === 'info' && (
        <Modal
          header={lang.usBankInfoPayment}
          subcomponents={ThemeModalDefault}
          onClose={() => setUsBankAction(undefined)}
          render={
            <FormWrapper>
              <table className={ThemeTableSimple}>
                <thead>
                  <tr>
                    <th>{lang.effectiveToDate}</th>
                    <th>{lang.expiryDate}</th>
                    <th>{lang.status}</th>
                    <th>{lang.amount}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{paymentInfo.effectiveUntil}</td>
                    <td>{paymentInfo.expirationDate}</td>
                    <td>{paymentInfo.status}</td>
                    <td>${paymentInfo.amount}</td>
                  </tr>
                </tbody>
              </table>
            </FormWrapper>
          }
        />
      )}
    </>
  );
};

export default ClaimJobPaymentItemUsBankPayment;
